import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { ArrowUpRight, Send, CheckCircle } from "lucide-react";

function FadeUp({ children, delay = 0, className = "" }) {
  const [ref, visible] = useScrollAnimation(0.1);
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: "easeOut" }}
      className={className}>
      {children}
    </motion.div>
  );
}

function Field({ label, type = "text", name, value, onChange, placeholder, required }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[11px] font-medium text-white/35 uppercase tracking-widest">
        {label}{required && <span className="text-lime ml-0.5">*</span>}
      </label>
      <input type={type} name={name} value={value} onChange={onChange}
        placeholder={placeholder} required={required}
        className="bg-white/5 border border-white/12 rounded-lg px-4 py-3 text-sm text-white
          placeholder-white/25 focus:outline-none focus:border-lime/50 transition-all duration-200" />
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState("idle");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    await new Promise((r) => setTimeout(r, 1300));
    setStatus("success");
  };

  return (
    <section id="contact" className="relative py-24 bg-ink overflow-hidden">
      {/* Watermark */}
      <div className="watermark-dark hidden md:block text-[9rem] xl:text-[12rem] right-0 -bottom-4">
        TekJovi
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <FadeUp className="mb-10 flex items-center justify-between">
          <span className="eyebrow text-white/35">06 — Get in touch</span>
          <span className="hidden sm:flex items-center gap-2 text-xs text-white/40">
            <span className="w-1.5 h-1.5 rounded-full bg-lime" />
            Open for new engagements — Q3 2026
          </span>
        </FadeUp>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: headline + info */}
          <div>
            <FadeUp delay={0.1}>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[0.96] tracking-[-0.03em] text-white mb-6">
                Ready to
                <br />
                build?
              </h2>
            </FadeUp>
            <FadeUp delay={0.18}>
              <p className="text-sm text-white/45 leading-relaxed max-w-sm mb-8">
                If you have a project worth building, we want to hear about it.
                We respond to every inquiry within one business day.
              </p>
            </FadeUp>
            <FadeUp delay={0.24}>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-12">
                {["hello@tekjovi.ai", "Office hours — Mon–Fri", "+1 (555) 800-NUWAI"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs text-white/50">
                    <span className="w-1 h-1 rounded-full bg-lime" />
                    {t}
                  </span>
                ))}
              </div>
            </FadeUp>

            <div className="grid sm:grid-cols-2 gap-10 pt-8 border-t border-white/10">
              <FadeUp delay={0.3}>
                <div className="eyebrow text-white/35 mb-3">What to include</div>
                <ul className="flex flex-col gap-2">
                  {["Project goals & timeline", "Budget range", "Technical constraints", "Decision makers involved"].map((b) => (
                    <li key={b} className="text-xs text-white/50 leading-relaxed">{b}</li>
                  ))}
                </ul>
              </FadeUp>
              <FadeUp delay={0.36}>
                <div className="eyebrow text-white/35 mb-3">Response path</div>
                <ul className="flex flex-col gap-2">
                  {["Reply within 1 business day", "Intro call within the week", "Scoped proposal in 3–5 days", "Kickoff once aligned"].map((b) => (
                    <li key={b} className="text-xs text-white/50 leading-relaxed">{b}</li>
                  ))}
                </ul>
              </FadeUp>
            </div>
          </div>

          {/* Right: form */}
          <FadeUp delay={0.2}>
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-7 sm:p-8">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div key="ok"
                    initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center justify-center py-14 text-center">
                    <div className="w-14 h-14 rounded-full bg-lime/15 flex items-center justify-center mb-5">
                      <CheckCircle size={26} className="text-lime" />
                    </div>
                    <h3 className="text-white font-bold text-lg mb-2">Message sent!</h3>
                    <p className="text-white/45 text-sm max-w-xs">
                      Thanks for reaching out — we'll reply within one business day.
                    </p>
                    <button onClick={() => { setStatus("idle"); setForm({ name:"",email:"",company:"",message:"" }); }}
                      className="mt-6 text-sm text-lime hover:text-lime-400 font-medium transition-colors">
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <motion.form key="form" exit={{ opacity: 0 }} onSubmit={onSubmit} className="flex flex-col gap-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Full Name" name="name" value={form.name} onChange={onChange}
                        placeholder="Jane Smith" required />
                      <Field label="Work Email" type="email" name="email" value={form.email} onChange={onChange}
                        placeholder="jane@company.com" required />
                    </div>
                    <Field label="Company" name="company" value={form.company} onChange={onChange}
                      placeholder="Acme Corp" />
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[11px] font-medium text-white/35 uppercase tracking-widest">
                        Project Brief<span className="text-lime ml-0.5">*</span>
                      </label>
                      <textarea name="message" value={form.message} onChange={onChange}
                        placeholder="Tell us what you're building…"
                        required rows={5}
                        className="bg-white/5 border border-white/12 rounded-lg px-4 py-3 text-sm
                          text-white placeholder-white/25 focus:outline-none focus:border-lime/50
                          transition-all duration-200 resize-none" />
                    </div>

                    <button type="submit" disabled={status === "loading"}
                      className="btn-lime flex items-center justify-center gap-2 w-full py-3.5 rounded-full
                        font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed mt-1">
                      {status === "loading" ? (
                        <>
                          <motion.div className="w-4 h-4 border-2 border-ink/30 border-t-ink rounded-full"
                            animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }} />
                          Sending…
                        </>
                      ) : (
                        <>Send your pitch <ArrowUpRight size={15} /></>
                      )}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
